/**
 * Asset Detail Page
 * Displays complete asset information
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { StatusBadge } from '../../components/ui/status-badge';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { AssetFormDialog } from '../../components/assets/AssetFormDialog';
import { AssetDeleteDialog } from '../../components/assets/AssetDeleteDialog';
import { useAssetDetailStore } from '../../stores/asset-detail';
import { useAssetPhotosStore } from '../../stores/asset-photos';
import { useAuthStore } from '../../stores/auth';
import { Edit, Trash2, Package, MoreVertical, Pencil } from 'lucide-react';
import { formatCurrency, formatDateTime } from '../../lib/utils';
import { PhotoEditOverlay } from '../../components/assets/PhotoEditOverlay';
import type { LifecycleStatus } from '../../types/api';

export function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPhotoId, setEditingPhotoId] = useState<number | null>(null);

  const assetId = id ? parseInt(id, 10) : undefined;
  const setAssetId = useAssetDetailStore(s => s.setAssetId);
  const setPhotosAssetId = useAssetPhotosStore(s => s.setAssetId);
  useEffect(() => { setAssetId(assetId); setPhotosAssetId(assetId); }, [assetId, setAssetId, setPhotosAssetId]);
  useAssetDetailStore.live();
  useAssetPhotosStore.live();
  const asset = useAssetDetailStore(s => s.asset);
  const isLoading = useAssetDetailStore(s => s._loading);
  const photos = useAssetPhotosStore(s => s.photos);
  const photosLoading = useAssetPhotosStore(s => s._loading);

  const isAdmin = user?.role === 'Admin';
  const canEdit = isAdmin || user?.role === 'Editor';
  const canRotate = canEdit || user?.role === 'Photographer';
  const canDelete = isAdmin;

  function mapLifecycleStatus(status: LifecycleStatus): string {
    const statusMap: Record<LifecycleStatus, string> = {
      'Draft': 'draft',
      'Inventoried': 'inventoried',
      'Valued': 'valued',
      'Listed for Sale': 'listed-for-sale',
      'Under Offer': 'under-offer',
      'Sold': 'sold',
      'Payment Pending': 'payment-pending',
      'Delivered': 'delivered',
      'Disposed': 'disposed',
      'Archived': 'archived',
    };
    return statusMap[status];
  }

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="Loading..."
          breadcrumbs={[
            { label: 'Assets', href: '/assets' },
            { label: 'Loading...' },
          ]}
        />
        <main className="flex-1 overflow-y-auto bg-muted/40">
          <div className="container mx-auto p-6 lg:p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-48 rounded-lg bg-muted" />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="h-64 rounded-lg bg-muted" />
                <div className="h-64 rounded-lg bg-muted" />
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!asset) {
    return (
      <>
        <PageHeader
          title="Asset Not Found"
          breadcrumbs={[
            { label: 'Assets', href: '/assets' },
            { label: 'Not Found' },
          ]}
        />
        <main className="flex-1 overflow-y-auto bg-muted/40">
          <div className="container mx-auto p-6 lg:p-8">
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Asset not found</p>
                <p className="text-sm text-muted-foreground mb-4">
                  The asset you're looking for doesn't exist.
                </p>
                <Button onClick={() => navigate('/assets')}>
                  Back to Assets
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={asset.asset_nickname}
        description={`Asset #${asset.unique_identifier}`}
        breadcrumbs={[
          { label: 'Assets', href: '/assets' },
          { label: asset.asset_nickname },
        ]}
        uploadAssetId={assetId}
        actions={
          <div className="flex gap-2">
            {canEdit && (
              <Button onClick={() => setEditDialogOpen(true)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            {canDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">More actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        }
      />

      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="container mx-auto p-6 lg:p-8">
          <div className="flex flex-col gap-6">
            {/* Hero Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-2xl">{asset.asset_nickname}</CardTitle>
                    <StatusBadge status={mapLifecycleStatus(asset.lifecycle_status) as any}>
                      {asset.lifecycle_status}
                    </StatusBadge>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Asset ID</div>
                    <div className="font-mono text-lg font-bold">
                      {asset.unique_identifier}
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Details Grid */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>Category and classification details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm font-medium">Category</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.category_name || 'Not assigned'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">Subcategory</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.subcategory_name || 'Not assigned'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">Disposition Status</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.disposition_status || 'N/A'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Location */}
              <Card>
                <CardHeader>
                  <CardTitle>Location</CardTitle>
                  <CardDescription>Physical location details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm font-medium">Street Address</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.street_address || 'Not specified'}
                    </div>
                  </div>
                  {asset.street_address_other && (
                    <div>
                      <div className="text-sm font-medium">Other Address</div>
                      <div className="text-sm text-muted-foreground">
                        {asset.street_address_other}
                      </div>
                    </div>
                  )}
                  {asset.specific_location && (
                    <div>
                      <div className="text-sm font-medium">Specific Location</div>
                      <div className="text-sm text-muted-foreground">
                        {asset.specific_location}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Financials */}
              <Card>
                <CardHeader>
                  <CardTitle>Financials</CardTitle>
                  <CardDescription>Pricing and sale information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm font-medium">Asking Price</div>
                    <div className="text-lg font-semibold">
                      {formatCurrency(asset.asking_price)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">Lowest Acceptable Price</div>
                    <div className="text-sm text-muted-foreground">
                      {formatCurrency(asset.lowest_acceptable_price)}
                    </div>
                  </div>
                  {asset.current_highest_offer !== null && (
                    <div>
                      <div className="text-sm font-medium">Current Highest Offer</div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(asset.current_highest_offer)}
                      </div>
                    </div>
                  )}
                  {asset.final_sale_price !== null && (
                    <div>
                      <div className="text-sm font-medium">Final Sale Price</div>
                      <div className="text-lg font-semibold text-success-600">
                        {formatCurrency(asset.final_sale_price)}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Workflow */}
              <Card>
                <CardHeader>
                  <CardTitle>Workflow</CardTitle>
                  <CardDescription>Responsibility and sharing settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm font-medium">Responsible Person</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.responsible_person_name || 'Not assigned'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">External Sharing</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.share_with_external ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Marketing Description */}
            {asset.marketing_description && (
              <Card>
                <CardHeader>
                  <CardTitle>Marketing Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: asset.marketing_description }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Photo Gallery */}
            <Card>
              <CardHeader>
                <CardTitle>Photos</CardTitle>
                <CardDescription>Asset images and documentation</CardDescription>
              </CardHeader>
              <CardContent>
                {photosLoading ? (
                  <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                    Loading photos...
                  </div>
                ) : photos && photos.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {photos.map((photo) => {
                      const rotation = photo.rotation_pending || 0;
                      const isRightAngle = Math.abs(rotation % 180) === 90;

                      return (
                      <div key={photo.photo_id} className="group relative">
                        <div
                          className={`overflow-hidden rounded-lg border ${
                            isRightAngle ? 'flex h-64 aspect-square items-center justify-center' : 'h-64 w-fit'
                          }`}
                        >
                          <img
                            src={photo.photo_url}
                            alt={photo.filename || 'Asset photo'}
                            className={isRightAngle ? 'h-full w-full object-contain' : 'h-full w-auto'}
                            style={rotation ? { transform: `rotate(${rotation}deg)` } : undefined}
                          />
                        </div>
                        {canRotate && (
                          <button
                            onClick={() => setEditingPhotoId(photo.photo_id)}
                            className="absolute top-2 left-2 rounded-md bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {photo.is_primary && (
                          <div className="absolute top-2 right-2 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
                            Primary
                          </div>
                        )}
                        {isAdmin && photo.lot_match_method === 'qr' && (
                          <div className="absolute bottom-2 left-2 rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white">
                            QR
                          </div>
                        )}
                        {isAdmin && photo.lot_match_method === 'timeline' && (
                          <div className="absolute bottom-2 left-2 rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white">
                            Timeline
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                    No photos uploaded
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Change Log Placeholder */}
            <Card>
              <CardHeader>
                <CardTitle>Change History</CardTitle>
                <CardDescription>Track all modifications to this asset</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                  Change log will appear here
                </div>
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{formatDateTime(asset.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span>{formatDateTime(asset.updated_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created By</span>
                  <span>{asset.created_by_name || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      {asset && (
        <>
          <AssetFormDialog
            mode="edit"
            asset={asset}
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
          />

          <AssetDeleteDialog
            asset={asset}
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          />
        </>
      )}

      {editingPhotoId !== null && photos && (() => {
        const livePhoto = photos.find((p) => p.photo_id === editingPhotoId);
        return livePhoto ? (
          <PhotoEditOverlay photo={livePhoto} onClose={() => setEditingPhotoId(null)} />
        ) : null;
      })()}
    </>
  );
}
